import { Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useAgentSkills,
  useProjectSkillBindings,
  useBindProjectSkill,
  useUnbindSkill,
} from '../hooks/useAgentSkills';

/**
 * Manage which installed Agent Skills are bound to a project. Bound skills are
 * materialized into every instance spawned in the project. Modeled on the
 * project settings sections (ProjectMcpServers).
 */
export default function ProjectSkills({ projectId }: { projectId: string }) {
  const { data: skills } = useAgentSkills(projectId);
  const { data: bindings } = useProjectSkillBindings(projectId);
  const bind = useBindProjectSkill(projectId);
  const unbind = useUnbindSkill(projectId);

  const bindingBySkillId = new Map(
    (bindings ?? []).filter((b) => b.enabled).map((b) => [b.skillId, b]),
  );

  const toggle = (skillId: string) => {
    const existing = bindingBySkillId.get(skillId);
    if (existing) {
      unbind.mutate(existing.id);
    } else {
      bind.mutate({ skillId, enabled: true });
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-violet-500" />
          <h3 className="font-semibold text-slate-900">Agent Skills</h3>
        </div>
        <Link
          to="/agent-skills"
          className="text-xs font-medium text-slate-500 hover:text-slate-700"
        >
          Manage skills
        </Link>
      </div>

      {!skills || skills.length === 0 ? (
        <p className="text-sm text-slate-400">
          No skills installed.{' '}
          <Link to="/agent-skills" className="text-slate-600 underline">
            Install one
          </Link>{' '}
          to bind it to this project.
        </p>
      ) : (
        <div className="space-y-2">
          {skills.map((skill) => {
            const isBound = bindingBySkillId.has(skill.id);
            return (
              <label
                key={skill.id}
                className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-2.5 hover:border-slate-300"
              >
                <input
                  type="checkbox"
                  checked={isBound}
                  onChange={() => toggle(skill.id)}
                  className="rounded border-slate-300"
                />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-slate-900">
                    {skill.name}
                  </span>
                  <span className="block truncate font-mono text-[11px] text-slate-400">
                    .claude/skills/{skill.slug}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
